import React from 'react';
import { DollarSign, Users, TrendingUp, Building2, CreditCard, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAdminMetrics, type AdminMetrics } from '../../hooks/useAdminMetrics';

export function AdminMetrics() {
  const { data: metrics, isLoading, error } = useAdminMetrics();

  // Default metrics to show cards even if data fails to load
  const defaultMetrics: AdminMetrics = {
    mrr: 0,
    mrrGrowth: 0,
    monthlyActiveUsers: 0,
    mauGrowth: 0,
    averageGymMonthlyRevenue: 0,
    totalGymRevenue: 0,
    totalGyms: 0,
    activeGyms: 0,
    totalUsers: 0,
    activeSubscriptions: 0,
  };

  const displayMetrics = metrics || defaultMetrics;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  return (
    <>
      {error && (
        <div className="mb-4 p-4 bg-[#9B1421]/20 border border-[#9B1421] rounded-lg">
          <p className="text-rollops-accent text-sm">
            Error loading metrics: {error instanceof Error ? error.message : 'Unknown error'}. Showing default values.
          </p>
        </div>
      )}
      {isLoading && (
        <div className="flex items-center justify-center py-4 mb-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
          <span className="ml-3 text-[#A9A3A3]">Loading metrics...</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* MRR Card */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#A9A3A3] flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Monthly Recurring Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#EBFFEE] mb-2">
              {formatCurrency(displayMetrics.mrr)}
            </div>
            {displayMetrics.mrrGrowth !== 0 && (
              <div className={`text-sm flex items-center gap-1 ${displayMetrics.mrrGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                <TrendingUp className={`w-4 h-4 ${displayMetrics.mrrGrowth < 0 ? 'rotate-180' : ''}`} />
                {formatPercent(displayMetrics.mrrGrowth)} vs last month
              </div>
            )}
          </CardContent>
        </Card>

        {/* Monthly Active Users Card */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#A9A3A3] flex items-center gap-2">
              <Users className="w-4 h-4" />
              Monthly Active Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#EBFFEE] mb-2">
              {displayMetrics.monthlyActiveUsers.toLocaleString()}
            </div>
            {displayMetrics.mauGrowth !== 0 && (
              <div className={`text-sm flex items-center gap-1 ${displayMetrics.mauGrowth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                <TrendingUp className={`w-4 h-4 ${displayMetrics.mauGrowth < 0 ? 'rotate-180' : ''}`} />
                {formatPercent(displayMetrics.mauGrowth)} vs last month
              </div>
            )}
          </CardContent>
        </Card>

        {/* Average Gym Monthly Revenue Card */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#A9A3A3] flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Avg Gym Monthly Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#EBFFEE] mb-2">
              {formatCurrency(displayMetrics.averageGymMonthlyRevenue)}
            </div>
            <div className="text-sm text-[#A9A3A3]">
              Total: {formatCurrency(displayMetrics.totalGymRevenue)}
            </div>
          </CardContent>
        </Card>

        {/* Total Gyms Card */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#A9A3A3] flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Total Gyms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#EBFFEE] mb-2">
              {displayMetrics.totalGyms}
            </div>
            <div className="text-sm text-[#A9A3A3]">
              {displayMetrics.activeGyms} active
            </div>
          </CardContent>
        </Card>

        {/* Total Users Card */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#A9A3A3] flex items-center gap-2">
              <Users className="w-4 h-4" />
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#EBFFEE] mb-2">
              {displayMetrics.totalUsers.toLocaleString()}
            </div>
          </CardContent>
        </Card>

        {/* Active Subscriptions Card */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[#A9A3A3] flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Active Subscriptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-[#EBFFEE] mb-2">
              {displayMetrics.activeSubscriptions.toLocaleString()}
            </div>
            <div className="text-sm text-[#A9A3A3]">
              SaaS subscriptions
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

